import type { Vehicle, Weapon } from '../content/types';

let nextId = 1;
export function newId(): number { return nextId++; }

export interface WeaponSlot { def: Weapon; mag: number; reserve: number /* -1 = infinite */; /** (owner, Sep 30) the attachments on it, item ids by slot (game/attachments.ts) */ mods?: Partial<Record<import('../content/types').AttachSlot, string>> }

/** (Sep 28) an activity the arms show: picking a lock, a wrench at a car, a new magazine, a bite, a drink, a bandage, cuffed, hands up */
export type PersonAct = 'pick' | 'repair' | 'reload' | 'eat' | 'drink' | 'heal' | 'cuffed' | 'hands' | /** (Sep 30) */ 'bino' | /** (owner, Sep 28: "add all the other activities") */ 'climb' | 'cuffing' | 'sit' | 'dance' | 'sneak' | 'phone';

export interface Person {
  kind: 'person';
  id: number;
  x: number; y: number;
  angle: number; // facing, radians, 0 = up (-y)
  r: number;
  hp: number; maxHp: number;
  weapons: WeaponSlot[];
  /** non-weapon inventory: item id → count (cash, food, gear, valuables) */
  items: Record<string, number>;
  /** survival meters 0..1 (players only) */
  food: number; water: number;
  /** parachute deployed (falling slowly, steerable) */
  chute: boolean;
  /** a flashlight from the pockets switched on (a cone in the dark; L on foot, or the pocket row) */
  torch?: boolean;
  /** (owner, Sep 24) the radar detector switched off (it is on from the moment it is in the pockets; Y or the Car app switches it) */
  radarOff?: boolean;
  /** (owner, Sep 25) flying a drone from where they stand (the sticks are on the drone): the body crouches over the controller, seen by everyone */
  droneEye?: boolean;
  /** (owner, Sep 25) the drone in the pockets: its charge 0..1 (undefined = full), and where one that came down away from you lies (a pile to walk to) */
  droneCharge?: number; droneAt?: [number, number];
  /** (owner, Sep 17) the mic is open (voice between players) — and, for me, how many players can hear it right now */
  talk?: boolean; talkHeard?: number;
  /** (owner, Sep 14) at a harvester's wheel: the tree the boom has locked on to (tile) and how much of it is left, 1 = untouched */
  lockTree?: [number, number, number];
  /** under the surface (Z while swimming; Q surfaces): out of sight, slow, on held breath or the scuba tank */
  dive?: boolean;
  /** held breath 0..1 (BREATH_S seconds under; recovers at the surface) */
  breath: number;
  /** the scuba tank's air 0..1 (with a scuba_set in the pocket; refilled or bought at the marina) */
  air?: number;
  /** inside a building: its key, and the floor (0 = ground) */
  inside?: string;
  floor: number;
  /** seconds spent on the stairs with ▲/▼ held */
  climb?: number;
  /** riding an elevator cab (building key): the cab moves the floor, the rider stays on the cell */
  inCab?: string;
  weaponIndex: number;
  fireCooldown: number;
  reloadLeft: number;
  /** game time of the last run-over hit (a car passing over counts once, not every frame) */
  ranOverAt?: number;
  /** when hurting this person last cost karma: a shotgun is eight pellets, and eight points would be absurd */
  hurtKarmaAt?: number;
  /** melee swing animation time left (s) */
  swing?: number;
  /** (owner, Sep 28: C3 "recoil, shells, smoke, a knockback and sparks") shots fired and blows taken, counted, and when the last came
   *  (game time) — the renderer plays a kick and a knockback on each new one; `hurtA` the way the blow pushed (radians, 0 = -y) */
  shotN?: number; shotT?: number; hurtN?: number; hurtT?: number; hurtA?: number;
  /** (owner, Sep 28: "animations for various activities: fixing a car, picking a lock, jump, what else?") what the hands are busy with, for
   *  everyone to see (on the wire `ac`): the renderer poses the arms for it; `actUntil`/`actKind` a moment's act (a bite, a drink, a
   *  bandage; a keeper's hands up while held up) until then */
  act?: PersonAct; actUntil?: number; actKind?: PersonAct;
  /** (Sep 28) a loot bag over the shoulder: the vault's bags on a heist, a till being emptied, a burglar on the way out (on the wire `lb`) */
  loot?: boolean;
  /** (owner, Sep 29) on a building's fire escape: its key ("x,y") and the landing (floor) you stand at — physics.ts keeps you on its iron */
  esc?: { key: string; level: number };
  /** (Sep 30) stunned by a flashbang until this game time: an NPC stands dazed, doing nothing */
  stunUntil?: number;
  /** (owner, Sep 30: gear) what each armour item worn has left to soak up (item id → hp), what everyone sees of it (vest level ×4 + helmet level), night vision down, binoculars up */
  armorWear?: Record<string, number>; armorV?: number; nv?: boolean; bino?: boolean;
  /** (Sep 30) the weapon in hand's attachments as everyone sees them: 1 a laser, 2 a suppressor (on the wire `mz`) */
  modsV?: number;
  /** (Sep 30) a mission's guard (game/missions.ts): whose mission, its post and facing, how far it sees, a patrol between spots, the alarm */
  mg?: { hurt?: number; holder: string; post: [number, number]; face: number; watch: number; cool: number; alarm?: boolean; patrol?: [number, number][]; leg?: number; pause?: number };
  /** (B) at the gym: the exercise under way (game.ts stepWorkout), for the renderer */
  workout?: WorkoutKind;
  /** (owner, Sep 25) watching TV: the set's tile, the surf's seed and its start (game time) — game/tv.ts derives the channels from it */
  tv?: import('./tv').TvWatch;
  /** (owner, Sep 23) a player's stamina 0…1 (undefined = full — the townsfolk never tire), out of breath until a quarter is back, and the lower-body boost on foot speed */
  stamina?: number; winded?: boolean; legs?: number;
  firePrev: boolean;
  vehicle?: VehicleEntity;
  /** height above ground (jumping / standing on a car) and vertical speed */
  z: number; vz: number;
  isPlayer: boolean;
  dead: boolean;
  deadFor: number;
  walker?: WalkerAi;
  /** what they look like / are (NPC): a house guard, a burglar */
  role?: 'guard' | 'burglar' | 'police' | 'fare' | 'resident' | 'keeper' | 'bounty' | 'gang' | 'animal' | 'mechanic' | 'soldier';
  /** (owner, Sep 16) the mechanic on a shop's payroll: the property key */
  mechanic?: string;
  /** gang member: which gang, where the group hangs out, jacket colour (also on the wire) */
  gang?: { id: string; home: [number, number]; color: string; idle: number };
  /** indoor navigation state (BFS path over the floor plan) */
  nav?: { goal: [number, number]; path: [number, number][]; stuck: number; last?: [number, number] };
  /** lives/works inside a building: wanders its floor plan (keepers stand at their spot) */
  resident?: { key: string; goal?: [number, number]; idle: number; flee: number };
  /** police officer on foot (a raid): hunts a wanted player, indoors too */
  police?: { target: number; cool: number; t: number; goal?: [number, number]; bestD?: number; bestAt?: number; /** a beat cop who witnessed something: goes back to walking when it is over */ beat?: boolean; /** seconds within cuffing reach */ cuff?: number };
  /** (owner, Sep 25) a soldier of a guarded place (Area 69): its post or route, the fence it defends (sentries.ts) */
  sentry?: import('./sentries').SentryState;
  /** hired guard of a house (property key); stands at the door, shoots threats */
  guard?: { key: string; cool: number; post: [number, number] };
  /** night-time burglar: approaches a house, loots the stash, leaves */
  burglar?: { key: string; phase: 'approach' | 'inside' | 'leave'; t: number; goal: [number, number]; stuck: number; /** progress watchdog: best distance to the goal and when it was set */ bestD?: number; bestAt?: number };
  home?: string;
  /** a cat or a dog (animals.ts): its content id and what it is up to */
  animal?: import('./animals').AnimalState;
  /** what they wear (cosmetics.ts): skin tone, top, hat — players only */
  /** the locked door this step ran into (physics, transient — the game says why) */
  bumpedDoor?: string;
  look?: { skin?: string; top?: string; hat?: string; hair?: string; beard?: string; glasses?: string; body?: string };
  /** (B) Loops on the speaker: the song and its start, from the owner's browser; `phoneDial` = the phone's own speaker playing (faint to people within a couple of metres) */
  loopsSong?: string; loopsAt?: number; phoneDial?: number;
  /** a carried boom box playing station 1–4 (0/undefined = off); T cycles it on foot */
  radio?: number;
}

export interface WalkerAi { dx: number; dy: number; think: number; idle: number; /** (Sep 28) resting on a bench (its tile): sitting while `idle` runs */ sit?: [number, number]; /** the last benches it sat on or could not get to ("x,y"): not tried again, it walks on */ noSeat?: string[]; flee?: number; fx?: number; fy?: number; /** popping into a store: walking to its doorstep, then inside for a while (`shop` = building key, `sx,sy` the doorstep, `out` = where to step out to) */ shop?: string; sx?: number; sy?: number; out?: [number, number]; /** crossing a street: stepped off the kerb / seconds waited for a gap */ stepped?: boolean; waited?: number }
export interface DriverAi {
  axis: 'v' | 'h'; dir: 1 | -1;
  /** police car that already sent its officers in on foot */
  unloaded?: boolean;
  /** intersection currently being handled (key) so we decide once */
  decided?: string;
  /** pending turn: new axis/dir and the perpendicular lane coordinate at which to switch */
  turn?: { axis: 'v' | 'h'; dir: 1 | -1; at: number };
  wait: number;
  /** trains: the locomotive's breadcrumb trail (world points every ~1 m, newest last) so wagons follow its path round corners */
  trail?: { x: number; y: number; a: number }[];
  /** buses: seconds left standing at a stop, and the stop ("x,y") last served so it is not served twice */
  stopFor?: number; lastStop?: string;
  /** trains: seconds nose to nose with an oncoming train, whether this one backed off last time, seconds dwelt at the end of the line */
  headOn?: number; yielded?: boolean; dwell?: number; lastTurn?: string;
  /** debug: why the car is (not) moving this step */
  why?: string;
  /** debug: the vehicle we are queued behind */
  blocker?: VehicleEntity;
  gridlock?: number;
  offLane?: number;
  /** seconds waited at the current red light (safety valve against a light that never turns) */
  redFor: number;
  lost: number;
  blockedFor: number;
  overtake: number;
  stuck: number;
  reverse: number;
  ignorePeds: number;
  /** wants to honk (set by the driver AI when a player has been blocking it; the game turns it into a horn event) */
  honk?: boolean; honkAt?: number; lastHorn?: string;
  /** gridlock breaker: seconds left of creeping through other cars as if they were not there (they get shoved) */
  ignoreCars?: number;
  /** (owner, Sep 12) metres the lane target is shifted sideways (right positive) to edge past something that sits on one side of the path; decays when nothing needs it */
  dodge?: number;
  /** (Sep 22) the shift was a pull-over's — off the road on purpose — and is not held to the road's room */
  dodgeOff?: true;
  /** (owner, Sep 12) seconds spent giving way at a crossing (priority, a left turn, a full box); past a limit the car goes anyway for `pushOn` seconds */
  yieldFor?: number; pushOn?: number;
  /** seconds left of preferring a turn over straight on: the road ahead is jammed */
  rethink?: number;
  /** (owner, Sep 12) seconds left of being pulled over to the kerb for a siren — behind us or oncoming on our road */
  pullOver?: number;
  /** (Sep 22) with `pullOver` for a siren: where it is — behind us going our way, or coming at us — and whether the road has room for it to pass a car at the kerb (then the car keeps rolling) */
  sirenFrom?: 'behind' | 'oncoming'; sirenRoom?: boolean;
  /** (Sep 22) seconds spent giving way to the current siren, and seconds left of ignoring sirens after one never passed */
  pullFor?: number; sirenTired?: number;
  /** with `pullOver`: the lateral target from the road's centre line (right positive; negative: the left verge, when the right is fenced) — the kerb for a siren, the verge to let an oncoming car by on a road too narrow for two */
  pullOff?: number;
  /** (Sep 17) giving way on a narrow road: how far ahead the verge is free for the whole car — it rolls on that far before it stops */
  pullAhead?: number;
  /** (owner, Sep 13) seconds spent within twelve metres of an anchor point; past a minute the car gives up its trip and parks on the verge */
  noProgress?: number; progAt?: number; progPx?: number; progPy?: number; giveUp?: boolean;
  /** seconds until the next courtesy stop for a car waiting at a side road (one every twenty) */
  courtesyCool?: number;
  /** the player head-on: headlights flashed (toggles left), the state to put back, and the horn's cooldown */
  flash?: number; flashLights?: boolean; flashT?: number; hornCool?: number; /** (Sep 14) seconds until the lights may flash again: once an encounter */ flashCool?: number;
  /** (owner, Sep 13) stuck on a tree: which side it is on (right positive), how many times running the car has been stuck here, where; the reverse's manner — straight first, then a swing with the wheels turned to the obstacle so the nose comes away */
  stuckSide?: -1 | 0 | 1; stuckCount?: number; stuckX?: number; stuckY?: number; reverseMode?: 'straight' | 'swing';
  /** headway watch for cars on a call: where it was, and seconds since that sample */
  progX?: number; progY?: number; progT?: number;
  /** seconds spent behind a standing car without getting anywhere */
  jammed?: number;
  /** pulling into a parking slot (parking.ts): the slot and seconds spent on it */
  park?: { x: number; y: number; angle: number; key: string; t: number };
  /** (Sep 22) the lane's bend read last tick — tiles across per tile along, signed — and whether the car is swinging through the arc of a turn (src/game/ai.ts) */
  bend?: number; turning?: true; /** the cross-track error to the lane, smoothed over a third of a second */ xte?: number;
  /** police pursuit: drive straight at the player */
  chase?: boolean;
  /** player id being pursued */
  target?: number;
  shootCooldown?: number;
}

export interface VehicleEntity {
  kind: 'vehicle';
  /** (owner, Sep 25: Area 69's jeep) drives a loop of world points on its own (sentries.ts stepPatrol) until a player takes it */
  patrol?: { route: [number, number][]; leg: number; stuck: number; back: number };
  /** (owner, Sep 25) a place's crewed vehicle: mans its gun while the site's alarm is up (sentries.ts) */
  crew?: { site: string; weapon: string; damage: number; rate: number; cool: number };
  /** (owner, Sep 25) a place's vehicle nobody gets into while that site's alarm is up (the saucer): the site's key */
  lockdown?: string;
  id: number;
  def: Vehicle;
  x: number; y: number;
  angle: number;
  speed: number;   // along heading, m/s (negative = reversing)
  /** a hovering helicopter's sideways speed, m/s (physics.ts) */
  lat?: number;
  /** tyres losing it (0..1): wheelspin under power, locked wheels, a slide — the squeal and the marks (physics.ts) */
  skid?: number;
  alt: number;     // metres above ground
  hp: number;
  driver?: Person;
  passengers: Person[];
  wrecked: boolean;
  home: string;    // chunk key it was spawned in (for unloading)
  /** a wagon: the locomotive's id and its place in the train (1 = first behind the loco) */
  coupled?: { lead: number; index: number };
  /** (B) trailers (trailer.ts): the trailer this car tows / the car towing this trailer; the boat on a trailer's bed / the trailer a boat rides on */
  tow?: number; towedBy?: number; cargo?: number; onTrailer?: number;
  /** (owner, Sep 13) logs on a logging vehicle's bed */
  logs?: number;
  /** (owner, Sep 22) pieces of a place's stuff aboard a cargo van/truck (game/stuff.ts), prop ids */
  stuff?: string[];
  /** (owner, Sep 16) bin bags in a garbage truck (def.bags is the cap) */
  bags?: number;
  /** (owner, Sep 16) a customer's car on a mechanic shift: the mechanic's person id, and when it was finished (it drives off a few seconds later) */
  customer?: number; customerDone?: number;
  /** (owner, Sep 16) a payroll mechanic's work on it: seconds into a wreck's rebuild, and the repair bill run up so far (whole dollars are taken as they fall due) */
  rebuildT?: number; bill?: number;
  /** (B) Loops on this stereo: the song playing and when it started (game time), told by the owner's browser so passers-by hear the same track */
  loopsSong?: string; loopsAt?: number;
  fireCooldown: number;
  firePrev: boolean;
  /** last collision impulse time, for sound/fx */
  bumped: number;
  /** what the last knock was against, for the sound it makes (docs/DESIGN_CRASH_SFX.md) — set by updateVehicle, read the same tick */
  hitWhat?: string;
  /** when the last scrape was sounded: a graze runs frame after frame, and one grind per second is a graze, sixty is a chainsaw */
  scrapeAt?: number;
  /** seconds spent in deep water (land vehicles sink, then wreck) */
  wet: number;
  /** seconds since it became a wreck (tow trucks come after a while) */
  wreckedFor?: number;
  /** an aircraft wrecked in the air: still coming down (it blows up where it lands: Game.wreckDown) */
  falling?: boolean;
  /** tuning (owned cars): engine 0–3 (+15 % speed and acceleration a level), armour 0–2 (+50 % hp a level) */
  mods?: { engine: number; armour: number; /** (B) stolen licence plates fitted (a mission's reward, never sold): the cameras cannot trace this car to you */ plates?: boolean; /** the multipliers the shop applied (economy.tuning gains), so physics needs no registry */ eng?: number; hpMul?: number };
  /** fuel / charge, 0..1 */
  fuel: number;
  /** an NPC car standing in a parking lot: game time it pulled in (it leaves again after a while)  */
  parkedAt?: number;
  /** headlights: undefined = automatic (on after dark), true/false = the driver's choice (L) */
  lights?: boolean;
  /** siren and light bar on (emergency / service vehicles, H) */
  siren?: boolean;
  /** the windows down (O, or the car list): the stereo reaches the street twice as far, and the rings around the car say so */
  windows?: boolean;
  /** (owner, Sep 24) cruise control: the gas takes the car up to the road's speed limit and no further (physics.ts) */
  cruise?: boolean;
  /** radio station 1–3 (T cycles; 0/undefined = off) — audio only, for whoever sits in it and passers-by */
  radio?: number;
  /** a wreck that has been searched for loot (E) */
  searched?: boolean;
  /** bought at a dealer: the owner's token (kept loaded, saved with the player, marked on their map) */
  owner?: string;
  /** respray (cosmetic id, owned cars) */
  paint?: string;
  ai?: DriverAi;
  /** (owner, Sep 25) a drone in the air (src/game/drone.ts): who flies it and what it is doing */
  drone?: DroneCtl;
}

/** what a drone is doing: holding still (and flown by hand), rising after take-off, flying to a spot, coming back to its owner, landing, or out of battery and coming down */
export type DroneMode = 'hover' | 'rise' | 'goto' | 'return' | 'land' | 'flat';
/** the wire's mode codes (WireVehicle.dr): an index into this */
export const DRONE_MODES: DroneMode[] = ['hover', 'rise', 'goto', 'return', 'land', 'flat'];
export interface DroneCtl {
  /** the player (person id) it belongs to */
  of: number;
  mode: DroneMode;
  /** battery 0..1 */
  bat: number;
  /** the destination (goto) */
  tx?: number; ty?: number;
  /** its velocity, world axes (m/s) — a quadcopter goes any way without turning; `angle` only follows it for the picture */
  vx: number; vy: number;
  /** the item it came from (content/items, `flies`): what goes back in the pockets */
  item: string;
  /** (Sep 25) the police: when they last said something about it, and when an officer last fired at it (game time) */
  copAt?: number; shotAt?: number;
}

export interface Projectile {
  id: number;
  x: number; y: number; z: number;
  vx: number; vy: number;
  travelled: number; range: number;
  damage: number; splash: number;
  /** (Sep 25) the weapon's `tree` factor, when it has one: what the shot does to a tree it hits */
  tree?: number;
  ownerId: number;
  /** the player token this shot must never hurt (a house's defences vs its owner) */
  spare?: string;
  tracer: string;
  kind: NonNullable<Weapon['projectile']>;
  /** a homing shot: the aircraft it chases (vehicle id) */
  target?: number;
  /** (Sep 30) a thrown charge in flight (its weapon id): it lands or sticks where it stops (game/charges.ts landCharge) instead of going off */
  charge?: string;
  /** a flame tongue washes over things instead of stopping at the first: who it has already burnt (server side only) */
  burnt?: number[];
}

/** the exercises a workout cycles through (owner, Sep 8: "few different kinds of animations") */
export type WorkoutKind = 'squats' | 'curls' | 'bench' | 'lunges' | 'jog' | /** (Sep 25) */ 'bag' | 'sauna';

export interface Effect { x: number; y: number; z: number; r: number; t: number; life: number; kind: 'explosion' | 'spark' | 'smoke' | /** (Sep 30) a smoke grenade's cloud; a flashbang's burst */ 'cloud' | 'flash' }

export function makePerson(x: number, y: number, isPlayer = false): Person {
  return { kind: 'person', id: newId(), x, y, angle: 0, r: 0.35, hp: 100, maxHp: 100, weapons: [], items: {}, food: 1, water: 1, chute: false, breath: 1, floor: 0, weaponIndex: 0, fireCooldown: 0, reloadLeft: 0, firePrev: false, z: 0, vz: 0, isPlayer, dead: false, deadFor: 0 };
}

export function makeVehicle(def: Vehicle, x: number, y: number, angle: number, home: string): VehicleEntity {
  return { kind: 'vehicle', id: newId(), def, x, y, angle, speed: 0, alt: 0, hp: def.stats.hp, passengers: [], wrecked: false, home, fireCooldown: 0, firePrev: false, bumped: 0, wet: 0, fuel: 0.3 + ((Math.abs(x * 7 + y * 13 + def.id.length) * 2654435761) % 700) / 1000 };
}

export function forward(angle: number): [number, number] { return [Math.sin(angle), -Math.cos(angle)]; }

/** Corners of a vehicle's footprint in world tiles (front-left, front-right, rear-right, rear-left). */
export function vehicleCorners(v: VehicleEntity, shrink = 0.1): [number, number][] {
  const [w, l] = v.def.size;
  const hw = w / 2 - shrink, hl = l / 2 - shrink;
  const s = Math.sin(v.angle), c = Math.cos(v.angle);
  const pt = (lx: number, ly: number): [number, number] => [v.x + lx * c - ly * s, v.y + lx * s + ly * c];
  return [pt(-hw, -hl), pt(hw, -hl), pt(hw, hl), pt(-hw, hl)];
}

/** Is world point inside the vehicle's rectangle? */
export function pointInVehicle(v: VehicleEntity, px: number, py: number, pad = 0): boolean {
  const dx = px - v.x, dy = py - v.y;
  const s = Math.sin(v.angle), c = Math.cos(v.angle);
  const lx = dx * c + dy * s, ly = -dx * s + dy * c;
  return Math.abs(lx) <= v.def.size[0] / 2 + pad && Math.abs(ly) <= v.def.size[1] / 2 + pad;
}

/** How tall a vehicle is for walkers (metres): you can stand on the roof or jump over it. */
export function vehicleTop(v: VehicleEntity): number {
  switch (v.def.class) {
    case 'bike': return 0.9;
    case 'boat': return 0.8;
    case 'truck': case 'bus': case 'tank': return 2.6;
    case 'plane': case 'helicopter': return 1.6;
    case 'drone': return 0.3;
    default: return 1.4;
  }
}

/** A thing on the ground: a weapon (weaponId + ammo), 'health', or a pile of items (weaponId 'items' + items). */
export interface Pickup { id: number; x: number; y: number; weaponId: string; ammo: number; home: string; t: number; /** (owner, Sep 25) a drone that came down here: its battery 0..1 (home 'drone': kept loaded until somebody picks it up) */ charge?: number; /** (owner, Sep 13) too heavy for hands: only a logging vehicle loads it (a log) */ bulk?: boolean; /** who dropped it (home mydrop): they take it back only after stepping away (owner: no picking up what you just put down) */ by?: number; /** the dropper has stepped away since */ away?: boolean; items?: Record<string, number>; /** lying inside a building (key) on a floor: only somebody on that floor sees or takes it */ inside?: string; floor?: number; /** (B) on the lake bed (sunken loot): only a diver (p.dive) takes it */ deep?: boolean; /** (owner, Sep 16) a house's bin at the curb (the house key): items.garbage is the bags; a garbage truck loads it (garbage.ts) */ bin?: string }
